import type { FC } from 'react';
import { Heading } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import type { ResponsiveValue, SystemProps } from '@chakra-ui/react';
import { classNames } from '../../../utils/tailwind/classNames';
import type { CommonStyles } from '../../types';

export type TextAlign = 'left' | 'right' | 'center' | 'justify';
export type WhiteSpace = 'wrap' | 'no-wrap' | 'normal';
export type HeaderTextStyle = 'regular' | 'bold' | 'strong';

const fontWeights: Record<HeaderTextStyle, number> = {
    regular: 400,
    bold: 500,
    strong: 600,
};

const getFontWeight = (style?: HeaderTextStyle): number | string =>
    style ? (fontWeights[style] ?? 'inherit') : 'inherit';

export interface HeaderProps extends CommonStyles {
    as: 'h1' | 'h2' | 'h3' | 'h4' | 'header';
    children: React.ReactNode;
    style?: HeaderTextStyle;
    color?: string;
    variant?: 'header1' | 'header2' | 'header3' | 'header4';
    capitalized?: boolean;
    id?: string;
    align?: ResponsiveValue<TextAlign>;
    whiteSpace?: ResponsiveValue<WhiteSpace> | undefined;
    textTransform?: SystemProps['textTransform'];
    className?: string;
    type?: 'link' | 'normal';
    textAlign?: ResponsiveValue<TextAlign>;
}

export const Header: FC<HeaderProps> = ({
    color = '#323232',
    style = 'regular',
    children,
    as = 'h1',
    variant = 'header1',
    id,
    capitalized = false,
    margin,
    padding,
    align = 'left',
    whiteSpace = 'normal',
    textTransform,
    className,
    type,
    ...props
}) => (
    <Heading
        textAlign={align}
        data-testid={id}
        textStyle={`${type}-header`}
        as={as}
        className={classNames(className, capitalized ?? 'capitalize')}
        sx={{
            fontWeight: getFontWeight(style),
            margin,
            padding,
            whiteSpace: whiteSpace || 'normal',
            fontStyle: style,
        }}
        color={color}
        variant={variant}
        textTransform={textTransform}
        {...props}
    >
        {children}
    </Heading>
);

Header.propTypes = {
    style: PropTypes.oneOf(['regular', 'bold', 'strong']),
    color: PropTypes.string,
};

Header.defaultProps = {
    style: 'regular',
    color: undefined,
    capitalized: false,
    id: undefined,
    align: undefined,
    variant: undefined,
    whiteSpace: undefined,
    textTransform: undefined,
    className: undefined,
    type: undefined,
    textAlign: undefined,
};
